import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { AlertCircle, ChevronDown, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api/client';

const FIELD = 'w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition-all appearance-none placeholder:text-slate-400';
const LABEL = 'block text-xs font-bold text-slate-600 uppercase tracking-widest mb-2';
const ERR   = 'mt-1.5 text-xs text-red-500 font-medium';

const SelectWrapper = ({ children }) => (
  <div className="relative">
    {children}
    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
  </div>
);

const SymptomForm = ({ onSymptomAdded = null }) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { severity: '', category: '', description: '' } });

  const [submitError, setSubmitError] = useState('');
  const descriptionValue = watch('description', '');

  const onSubmit = async (data) => {
    setSubmitError('');

    // Derive a short title from the first sentence (backend auto-generates if omitted,
    // but this gives doctors a cleaner list entry)
    const firstSentence = data.description.split(/[.!?\n]/)[0].trim();
    const derivedTitle  = firstSentence.length > 60
      ? firstSentence.slice(0, 57) + '…'
      : firstSentence;

    try {
      const response = await api.post('/symptoms', {
        title:       derivedTitle || data.description.slice(0, 60),
        description: data.description,
        severity:    data.severity  || undefined,
        category:    data.category  || undefined,
      });
      // Success: toast persists after the modal closes; inline message would vanish
      toast.success('Your report has been sent to your care team.');
      reset();
      onSymptomAdded?.(response.data.data);
    } catch (err) {
      setSubmitError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Something went wrong. Please try again.'
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {submitError && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-100 rounded-xl">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">{submitError}</p>
        </div>
      )}

      {/* Severity + Category */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="sf-severity" className={LABEL}>
            Severity <span className="text-red-400 normal-case font-normal">*</span>
          </label>
          <SelectWrapper>
            <select
              id="sf-severity"
              {...register('severity', { required: 'Please select a severity' })}
              className={`${FIELD} pr-9 ${errors.severity ? 'border-red-300 focus:border-red-400 focus:ring-red-400/20' : ''}`}
            >
              <option value="">Select…</option>
              <option value="low">Mild</option>
              <option value="medium">Moderate</option>
              <option value="high">Serious</option>
              <option value="urgent">Severe</option>
            </select>
          </SelectWrapper>
          {errors.severity && <p className={ERR}>{errors.severity.message}</p>}
        </div>

        <div>
          <label htmlFor="sf-category" className={LABEL}>Category</label>
          <SelectWrapper>
            <select
              id="sf-category"
              {...register('category')}
              className={`${FIELD} pr-9`}
            >
              <option value="">Auto-detect</option>
              <option value="general">General</option>
              <option value="pain">Pain</option>
              <option value="respiratory">Respiratory</option>
              <option value="digestive">Digestive</option>
              <option value="neurological">Neurological</option>
              <option value="cardiac">Cardiac</option>
              <option value="skin">Skin</option>
              <option value="mental_health">Mental health</option>
              <option value="other">Other</option>
            </select>
          </SelectWrapper>
        </div>
      </div>

      {/* Description — the primary required input */}
      <div>
        <label htmlFor="sf-description" className={LABEL}>
          What's happening <span className="text-red-400 normal-case font-normal">*</span>
        </label>
        <textarea
          id="sf-description"
          {...register('description', {
            required: 'Please describe what you are experiencing',
            minLength: { value: 10, message: 'Please add a bit more detail (at least 10 characters)' },
            maxLength: { value: 2000, message: 'Maximum 2000 characters' },
          })}
          rows={5}
          className={`${FIELD} resize-none ${errors.description ? 'border-red-300 focus:border-red-400 focus:ring-red-400/20' : ''}`}
          placeholder="Describe your symptoms — when did it start, how does it feel, anything you've noticed…"
        />
        <div className="flex justify-between items-start mt-1.5">
          {errors.description
            ? <p className={ERR}>{errors.description.message}</p>
            : <span />}
          <span className={`text-xs tabular-nums ${descriptionValue.length > 1800 ? 'text-amber-500 font-semibold' : 'text-slate-400'}`}>
            {descriptionValue.length}/2000
          </span>
        </div>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-gradient-to-r from-blue-700 to-blue-600 text-white text-sm font-bold shadow-md hover:from-blue-800 hover:to-blue-700 hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isSubmitting
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
          : 'Report Symptom'}
      </button>
    </form>
  );
};

export default SymptomForm;
